import { getBaseUrl } from "../config.js";
import { generateKeyPair, importPrivateKey, signMessage } from "../crypto/index.js";
import { clearKey, getKey, saveKey } from "../storage/index.js";
import type { Brand } from "../ui/i18n.js";

export type DwtfConfig = {
  appId: string;
  apiUrl?: string;
  lang?: string;
  brand?: Brand;
  onSuccess?: (u: { userId: string; token: string }) => void;
  onError?: (e: Error) => void;
};

type RegisterRes = { user_id: string };
type Tok = { user_id: string; token: string };

export class DwtfAuth {
  appId: string;
  apiUrl: string;
  private onSuccess?: DwtfConfig["onSuccess"];
  private onError?: DwtfConfig["onError"];
  private token: string | null = null;
  private userId: string | null = null;
  /** Shared in-flight signIn — desktop+mobile widgets must not race challenges. */
  private signInFlight: Promise<{ userId: string; token: string; isNew: false }> | null = null;

  constructor(c: DwtfConfig) {
    this.appId = c.appId;
    this.apiUrl = getBaseUrl(c.apiUrl);
    this.onSuccess = c.onSuccess;
    this.onError = c.onError;
    this.hydrateSession();
  }

  getSession() {
    return this.userId && this.token ? { userId: this.userId, token: this.token } : null;
  }

  isExpired() {
    try {
      return sessionStorage.getItem(this.expiredKey()) === "1";
    } catch {
      return false;
    }
  }

  /** True if IndexedDB already holds a key (1 device = 1 key). */
  async hasStoredKey() {
    return !!(await getKey());
  }

  /**
   * Sign in with the stored key only — never generates a new keypair.
   * Use createAccount() / importAndSignIn() when there is no key.
   * Concurrent callers share one in-flight prove (MPA dual-mount safe).
   */
  async signIn() {
    if (this.signInFlight) return this.signInFlight;
    this.signInFlight = (async () => {
      try {
        const existing = this.getSession();
        if (existing) return { ...existing, isNew: false as const };
        const k = await getKey();
        if (!k?.privateKeyB64 || !k.publicKeyB64) throw new Error("no key");
        return {
          ...(await this.prove(k.userId, k.privateKeyB64, k.publicKeyB64)),
          isNew: false as const,
        };
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        this.onError?.(err);
        throw err;
      }
    })().finally(() => {
      this.signInFlight = null;
    });
    return this.signInFlight;
  }

  /**
   * Explicit new account. Refuses if a key is already stored
   * (call logout/clearKey first to reset the device).
   */
  async createAccount() {
    try {
      if (await getKey()) throw new Error("key exists");
      return this.register();
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      this.onError?.(err);
      throw err;
    }
  }

  async logout() {
    await clearKey();
    this.token = this.userId = null;
    this.clearSession();
    this.clearExpired();
  }

  async relogin() {
    this.token = this.userId = null;
    this.clearSession();
    this.clearExpired();
    return this.signIn();
  }

  markExpired() {
    this.token = this.userId = null;
    this.clearSession();
    try {
      sessionStorage.setItem(this.expiredKey(), "1");
    } catch {
      /* */
    }
  }

  /** Explicit restore — replaces any local key with the imported recovery pair. */
  async importAndSignIn(privateKeyB64: string, publicKeyB64: string) {
    try {
      await saveKey({ privateKeyB64, publicKeyB64, created_at: Date.now() });
      const r = await this.post<RegisterRes>("/v1/auth/register", {
        app_id: this.appId,
        public_key: publicKeyB64,
      });
      await saveKey({
        privateKeyB64,
        publicKeyB64,
        userId: r.user_id,
        created_at: Date.now(),
      });
      return this.prove(r.user_id, privateKeyB64, publicKeyB64);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      this.onError?.(err);
      throw err;
    }
  }

  async exportRecoveryKey() {
    const k = await getKey();
    if (!k) throw new Error("no key");
    return { privateKeyB64: k.privateKeyB64, publicKeyB64: k.publicKeyB64 };
  }

  /** sessionStorage key — survives MPA navigations in the same tab (ADR 0006). */
  private sessKey() {
    return "dwtf.s." + this.appId;
  }

  private expiredKey() {
    return "dwtf.x." + this.appId;
  }

  private hydrateSession() {
    try {
      const raw = sessionStorage.getItem(this.sessKey());
      if (!raw) return;
      const j = JSON.parse(raw) as { u?: string; t?: string };
      if (j.u && j.t) {
        this.userId = j.u;
        this.token = j.t;
      }
    } catch {
      /* private mode / unavailable */
    }
  }

  private persistSession() {
    if (!this.userId || !this.token) return;
    try {
      sessionStorage.setItem(this.sessKey(), JSON.stringify({ u: this.userId, t: this.token }));
      this.clearExpired();
    } catch {
      /* */
    }
  }

  private clearSession() {
    try {
      sessionStorage.removeItem(this.sessKey());
    } catch {
      /* */
    }
  }

  private clearExpired() {
    try {
      sessionStorage.removeItem(this.expiredKey());
    } catch {
      /* */
    }
  }

  private async register() {
    // Defense in depth: never overwrite an existing device key here.
    if (await getKey()) throw new Error("key exists");
    const p = await generateKeyPair();
    const r = await this.post<RegisterRes>("/v1/auth/register", {
      app_id: this.appId,
      public_key: p.publicKeyB64,
    });
    await saveKey({
      privateKeyB64: p.privateKeyB64,
      publicKeyB64: p.publicKeyB64,
      userId: r.user_id,
      created_at: Date.now(),
    });
    const session = await this.prove(r.user_id, p.privateKeyB64, p.publicKeyB64);
    return { ...session, isNew: true as const };
  }

  private async prove(userId: string | undefined, priv: string, pub: string) {
    let uid = userId;
    if (!uid) uid = await this.registerForApp(priv, pub);
    try {
      return await this.challengeAndVerify(uid, priv);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // One device key can serve multiple apps — re-bind when stored userId is from another app.
      if (!msg.includes("user does not belong to app") && !msg.includes("unknown user_id")) throw e;
      uid = await this.registerForApp(priv, pub);
      return this.challengeAndVerify(uid, priv);
    }
  }

  private async registerForApp(priv: string, pub: string) {
    const r = await this.post<RegisterRes>("/v1/auth/register", {
      app_id: this.appId,
      public_key: pub,
    });
    await saveKey({
      privateKeyB64: priv,
      publicKeyB64: pub,
      userId: r.user_id,
      created_at: Date.now(),
    });
    return r.user_id;
  }

  private async challengeAndVerify(uid: string, priv: string) {
    const ch = await this.post<{ challenge_id: string; nonce: string }>("/v1/auth/challenge", {
      app_id: this.appId,
      user_id: uid,
    });
    const sig = await signMessage(await importPrivateKey(priv), ch.nonce);
    const v = await this.post<Tok>("/v1/auth/verify", {
      challenge_id: ch.challenge_id,
      signature: sig,
    });
    this.userId = v.user_id;
    this.token = v.token;
    this.persistSession();
    const out = { userId: v.user_id, token: v.token };
    this.onSuccess?.(out);
    return out;
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    const c = new AbortController();
    const t = setTimeout(() => c.abort(), 15e3);
    try {
      const res = await fetch(this.apiUrl + path, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
        signal: c.signal,
      });
      if (!res.ok) {
        let d = res.statusText;
        try {
          const j = (await res.json()) as { error?: string };
          if (j.error) d = j.error;
        } catch {
          /* */
        }
        throw new Error(path + ": " + d);
      }
      return res.json() as Promise<T>;
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") throw new Error(path + ": timeout");
      throw e;
    } finally {
      clearTimeout(t);
    }
  }
}
